import { useEffect } from 'react';
import { useRouter } from 'next/router';
import useSWRInfinite from 'swr/infinite';

import { getAppliedFilters } from 'shared/utils/gridEventTrackingUtils';
import { getGlobalPricingHiddenFacets } from 'search/utils/getGlobalPricingHiddenFacets';
import { getSearchStateFromUrl } from 'search/utils/getSearchStateFromUrl';
import { getSearchTermFromConstructorRequest } from 'search/utils/getSearchTermFromConstructorRequest';
import { getSortOptions } from 'search/utils/getSortOptions';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IConstructorSearchResponse } from 'shared/types/ConstructorSearchResponse';
import { ISearchState } from 'search/types/SearchState';
import { RESULTS_PER_PAGE } from 'search/enums/constants';
import { getRegionPricingVariationsMap } from 'search/utils/getRegionPricingVariationsMap';
import { useConstructorClient } from 'shared/hooks/useConstructorClient';

const fetchSearchResults = (ConstructorClient, globalPricingId, page, searchState, searchQuery) =>
  ConstructorClient.search.getSearchResults(searchQuery, {
    resultsPerPage: RESULTS_PER_PAGE,
    page,
    ...getGlobalPricingHiddenFacets(globalPricingId),
    ...searchState,
    ...getRegionPricingVariationsMap(globalPricingId),
  });

const fetchBrowseResults = (
  ConstructorClient,
  globalPricingId,
  page,
  searchState,
  browseKey = 'group_id',
  browseValue = 'all',
) =>
  ConstructorClient.browse.getBrowseResults(browseKey, browseValue, {
    resultsPerPage: RESULTS_PER_PAGE,
    page,
    ...getGlobalPricingHiddenFacets(globalPricingId),
    ...searchState,
    ...getRegionPricingVariationsMap(globalPricingId),
  });

interface IUseInfiniteConstructorSearch {
  browseKey?: string;
  browseValue?: string;
  globalPricingId: string;
  fallbackData: Array<IConstructorSearchResponse | IConstructorBrowseResponse>;
}

export const useInfiniteConstructorSearch = ({
  browseKey = 'group_id',
  browseValue = 'all',
  globalPricingId,
  fallbackData,
}: IUseInfiniteConstructorSearch) => {
  const { query, asPath, pathname } = useRouter();
  const pathnameWithDynamicValuesIncluded: string = asPath.split('?')[0];
  const searchState: ISearchState = getSearchStateFromUrl(query, pathnameWithDynamicValuesIncluded);
  const searchTermFromRequest = getSearchTermFromConstructorRequest(fallbackData);
  const searchQuery = (query.query as string) || searchTermFromRequest;
  const ConstructorClient = useConstructorClient();

  const { data, size, setSize } = useSWRInfinite(
    (pageIndex: number, previousPageData) => {
      if (previousPageData && previousPageData.response.results.length === 0) return null; // reached the end
      const nextPage = pageIndex + 1;
      return [nextPage, JSON.stringify(searchState), query, ConstructorClient, asPath];
    },
    (page) =>
      searchQuery
        ? fetchSearchResults(ConstructorClient, globalPricingId, page[0], searchState, searchQuery)
        : fetchBrowseResults(
            ConstructorClient,
            globalPricingId,
            page[0],
            searchState,
            browseKey,
            browseValue,
          ),
    {
      keepPreviousData: true,
      fallbackData,
    },
  );
  const firstResponse = data[0]?.response;
  const firstRequest = data[0]?.request;
  const results = data.flatMap((d) => d?.response?.results);
  const facets = firstResponse?.facets;
  const sortOptions = getSortOptions({
    sortOptions: firstResponse?.sort_options,
    facets,
    globalPricingId,
    router: {
      pathname,
      asPath,
      query,
    },
  });
  const resultsCount = firstResponse?.total_num_results;
  const groups = firstResponse?.groups?.[0];
  const resultIds = data.map((d) => d.result_id);

  useEffect(() => {
    if (searchQuery) {
      ConstructorClient?.tracker?.trackSearchResultsLoaded(searchQuery, {
        num_results: resultsCount,
        item_ids: results.map((result) => result.data.id),
      });
    } else {
      ConstructorClient?.tracker?.trackBrowseResultsLoaded({
        url: window.location.href,
        filter_name: browseKey,
        filter_value: browseValue,
        result_count: resultsCount,
        result_page: size,
        result_id: resultIds[resultIds.length - 1],
        sort_order: firstRequest.sort_order,
        sort_by: firstRequest.sort_by,
        items: results.map((res) => ({ item_id: res.data?.id }))?.slice(-24),
        ...(getAppliedFilters(query) && { selected_filters: JSON.parse(getAppliedFilters(query)) }),
      });
    }
  }, [resultIds.join(',')]);

  return {
    facets,
    groups,
    resultIds,
    results,
    resultsCount,
    searchQuery,
    setSize,
    size,
    sortOptions,
  };
};
